import { useEffect, useMemo, useState } from "react";
import { api, pln } from "@/lib/api";
import { Trophy } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PL_MONTHS = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
const Q_MONTHS = ["sty–mar", "kwi–cze", "lip–wrz", "paź–gru"];
const pad = (n) => String(n).padStart(2, "0");

function buildOptions(gran) {
  const now = new Date();
  const y = now.getFullYear();
  const opts = [];
  if (gran === "quarter") {
    let qy = y, qq = Math.floor(now.getMonth() / 3) + 1;
    for (let i = 0; i < 8; i++) {
      opts.push({ value: `${qy}-Q${qq}`, label: `Q${qq} ${qy} (${Q_MONTHS[qq - 1]})` });
      qq--; if (qq === 0) { qq = 4; qy--; }
    }
  } else if (gran === "month") {
    for (let i = 0; i < 12; i++) {
      const d = new Date(y, now.getMonth() - i, 1);
      opts.push({ value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`, label: `${PL_MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    }
  } else if (gran === "year") {
    for (let i = 0; i < 4; i++) opts.push({ value: `${y - i}`, label: `${y - i}` });
  }
  return opts;
}

function rangeFor(gran, val) {
  if (gran === "all" || !val) return {};
  if (gran === "quarter") {
    const [yy, q] = val.split("-Q").map(Number);
    const startM = (q - 1) * 3;
    const endD = new Date(yy, startM + 3, 1);
    return { start: `${yy}-${pad(startM + 1)}-01`, end: `${endD.getFullYear()}-${pad(endD.getMonth() + 1)}-01` };
  }
  if (gran === "month") {
    const [yy, mm] = val.split("-").map(Number);
    const endD = new Date(yy, mm, 1);
    return { start: `${yy}-${pad(mm)}-01`, end: `${endD.getFullYear()}-${pad(endD.getMonth() + 1)}-01` };
  }
  if (gran === "year") return { start: `${val}-01-01`, end: `${Number(val) + 1}-01-01` };
  return {};
}

export const Ranking = ({ onPick, basis: initialBasis = "invoiced" }) => {
  const [gran, setGran] = useState("quarter");
  const [val, setVal] = useState(() => buildOptions("quarter")[0].value);
  const [basis, setBasis] = useState(initialBasis);
  const [ranking, setRanking] = useState([]);
  const options = useMemo(() => buildOptions(gran), [gran]);

  useEffect(() => { setBasis(initialBasis); }, [initialBasis]);

  useEffect(() => {
    const { start, end } = rangeFor(gran, val);
    api.get("/stats/ranking", { params: { start, end, date_basis: basis } }).then((r) => setRanking(r.data));
  }, [gran, val, basis]);

  const changeGran = (g) => { setGran(g); const o = buildOptions(g); setVal(o[0]?.value); };

  return (
    <div data-testid="ranking-table" className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="font-display text-sm font-bold uppercase tracking-wider">Ranking handlowców</h3>
        <div className="flex items-center gap-2">
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger data-testid="ranking-basis" className="w-[170px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="invoiced">Wg daty faktury</SelectItem>
              <SelectItem value="created">Wg daty dodania</SelectItem>
            </SelectContent>
          </Select>
          <Select value={gran} onValueChange={changeGran}>
            <SelectTrigger data-testid="ranking-granularity" className="w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="quarter">Kwartał</SelectItem>
              <SelectItem value="month">Miesiąc</SelectItem>
              <SelectItem value="year">Rok</SelectItem>
              <SelectItem value="all">Cały czas</SelectItem>
            </SelectContent>
          </Select>
          {gran !== "all" && (
            <Select value={val} onValueChange={setVal}>
              <SelectTrigger data-testid="ranking-period" className="w-[190px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-secondary text-muted-foreground font-mono text-[11px] uppercase tracking-wider">
          <tr><th className="text-left px-5 py-2">#</th><th className="text-left px-3 py-2">Handlowiec</th><th className="text-right px-3 py-2">Zamówienia</th><th className="text-right px-3 py-2">Sztuki</th><th className="text-right px-3 py-2">Netto</th><th className="text-right px-5 py-2">Prowizja</th></tr>
        </thead>
        <tbody>
          {ranking.length === 0 && (
            <tr><td colSpan={6} data-testid="ranking-empty" className="px-5 py-6 text-center text-muted-foreground">Brak zamówień w wybranym okresie</td></tr>
          )}
          {ranking.map((r, i) => {
            const leader = i === 0 && r.commission > 0;
            return (
            <tr key={r.employee_id} data-testid={`ranking-row-${i}`} onClick={() => onPick?.(r.employee_id)}
              className={`border-t border-border transition-colors ${onPick ? "cursor-pointer hover:bg-accent/60" : ""} ${leader ? "bg-amber-400/10" : ""}`}>
              <td className="px-5 py-2.5 font-mono">
                {leader
                  ? <span data-testid="ranking-leader-badge" className="inline-flex items-center gap-1.5 font-extrabold text-amber-400"><Trophy className="h-4 w-4" />1</span>
                  : <span className="text-amber-400">{i + 1}</span>}
              </td>
              <td className="px-3 py-2.5 font-medium">
                {leader
                  ? <span className="inline-flex items-center gap-2 font-bold">{r.name}<span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#0D1117]">Lider</span></span>
                  : r.name}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">{r.orders}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{r.qty}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{pln(r.net)}</td>
              <td className={`px-5 py-2.5 text-right tabular-nums font-semibold ${leader ? "text-amber-300 font-extrabold" : "text-amber-300"}`}>{pln(r.commission)}</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
